import {Component, ElementRef, OnDestroy, OnInit, Renderer2, isDevMode, Inject} from '@angular/core';
import {ExtensionService} from "./services/extension.service";
import {filter, retry, Subject, Subscription, take, throwError} from "rxjs";
import {NavigationEnd, Router} from "@angular/router";
import {initFlowbite} from "flowbite";

import { Store } from '@ngrx/store';
import { AppState } from './state/app.state';
import {SharedService} from "./services/shared.service";
import {EventTypes} from "./event-types";
import {EchoService} from "./services/echo.service";
import {AuthService} from "./services/auth.service";
import {TranslateService} from "@ngx-translate/core";
import {updateCompanyAssistantPrompt} from "./utils/update-company-assistant-prompt.util";
import {updateCompany} from "./state/report/report.actions";
import {Company} from "./interfaces/company.interface";
import {selectCompany} from "./state/report/report.selectors";
import {updateCompanyIcpScore} from "./utils/update-company-icp-score.util";
import { environment } from '../environments/environment';
import {PromptService} from "./services/prompt.service";
import {selectIsDarkMode} from "./state/ui/ui.selectors";
import {DOCUMENT} from "@angular/common";
import {takeUntil} from "rxjs/operators";

import { TourService } from './services/tour.service';
import {selectReport} from "./state/search/search.selectors";
import {updateReport} from "./state/search/search.actions";
import {SessionService} from "./services/session.service";
import {PopupService} from "./services/popup.service";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  // private wsSubscription: Subscription;
  // public messages: string[] = [];
  public error: string;

  title = 'SalesOMMO';
  private subscription: Subscription;
  chromeExtensionInstalled: boolean = true;

  user: any = null;

  company: Company | null = null;

  // private tokenSubscription: Subscription;

  isDevelopment = false;

  isDarkMode$ = this.store.select(selectIsDarkMode);

  private subscriptions: Subscription[] = [];

  private destroy$ = new Subject<void>();

  // Track tour state
  tourActive = false;
  tourManuallyStarted = false;

  constructor(
    private extensionService: ExtensionService,
    private router: Router,
    private store: Store<AppState>,
    private authService: AuthService,
    private sharedService: SharedService,
    private echoService: EchoService,
    private translate: TranslateService,
    private promptService: PromptService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private tourService: TourService,
    private sessionService: SessionService,
    private popupService: PopupService
  ) {

    this.subscription = this.extensionService.isChromeExtensionInstalled$.subscribe(value => {
      this.chromeExtensionInstalled = value;
    });

    // Set the default language to English
    this.translate.setDefaultLang('en');

    // If you want to immediately switch to English upon initialization (not just set as default)
    this.translate.use('en');

    /*
    this.isDevelopment = isDevMode();
    if (this.isDevelopment) {
      console.log('Running in development mode!');
    } else {
      console.log('Running in production mode!');
    }
    */

    // Initialize dark mode from store
    this.isDarkMode$.subscribe(isDark => {
      this.toggleDarkMode(isDark);
    });

    // Subscribe to tour active state
    this.tourService.tourActive$.subscribe(active => {
      this.tourActive = active;
    });

  }

  toggleDarkMode(isDark: boolean) {
    const hostElement = this.document.body;
    if (isDark) {
      this.renderer.addClass(hostElement, 'dark');
    } else {
      this.renderer.removeClass(hostElement, 'dark');
    }
  }

  ngOnInit(): void {
    // console.log('[app.component.ts] ngOnInit')

    // Check if we're in Chrome
    const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge/.test(navigator.userAgent);

    // Only check for extension in Chrome
    if (isChrome) {
      // Check extension with proper error handling
      try {
        this.extensionService.checkExtensionStatus();
      } catch (error) {
        console.error('Error checking extension status:', error);
      }
    }

    // Initialize Flowbite safely
    this.initializeFlowbite();

    // Manage subscriptions properly
    this.setupSubscriptions();

    // Check for potential issues with storage
    this.checkForStorageIssues();


    // Listen for route changes to potentially start the tour
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const route = event.urlAfterRedirects;

      // If navigation to dashboard and tour not manually started
      if ((route === '/dashboard' || route === '/') && !this.tourManuallyStarted) {
        // Check if user is new and should see the tour
        const isNewUser = !this.tourService.hasTourBeenCompleted();

        if (isNewUser && !this.tourActive) {
          // Wait for components to render
          setTimeout(() => {
            this.startTour(route);
          }, 800);
        }
      }
    });

  }

  /**
   * Start the tour - can be called from any component
   */
  startTour(route?: string) {
    // Don't start if already active
    if (this.tourActive) {
      return;
    }

    this.tourManuallyStarted = true;

    // Force the correct route before starting
    if (route) {
      this.tourService['currentPageSubject'].next(route);
    }

    // Start the tour
    this.tourService.startTour();
  }

  /**
   * Reset tour state - call when you want to allow the tour to show again
   */
  resetTourState() {
    this.tourManuallyStarted = false;
    this.tourService.resetTourStatus();
  }

  private initializeFlowbite(): void {
    // Try to initialize immediately but with error handling
    try {
      initFlowbite();
    } catch (error) {
      console.warn('Error initializing Flowbite, will retry:', error);
      // Retry after a short delay
      setTimeout(() => {
        try {
          initFlowbite();
        } catch (retryError) {
          console.error('Failed to initialize Flowbite after retry:', retryError);
        }
      }, 1000);
    }
  }

  private setupSubscriptions(): void {
    // Handle auth user
    this.authService.authUser().pipe(
      takeUntil(this.destroy$)
    ).subscribe(user => {
      this.user = user;
    });

    // Handle login status
    this.authService.isLoggedIn().pipe(
      takeUntil(this.destroy$)
    ).subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.listenToEvents();
      }
    });

    // Handle company selection
    this.store.select(selectCompany).pipe(
      takeUntil(this.destroy$)
    ).subscribe((company) => {
      this.company = company;
    });
  }

  private checkForStorageIssues(): void {
    // console.log('[app.component.ts] checkForStorageIssues')

    // Check if localStorage might be causing problems
    try {
      // Try accessing localStorage
      const testKey = '_test_app_storage';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      // Maybe localStorage is disabled or full - inform the user
      alert('Your browser storage appears to be having issues. Please try clearing your browser data or using incognito mode.');
    }
  }

  private listenToEvents(): void {

    // console.log('listenToEvents')
    // console.log('User ID: ', this.user.id)

    // First, clean up any existing listeners to prevent duplicates
    const echo = this.echoService.getEcho();
    if (!echo) return;

    if (this.user && this.user.id) {
      try {
        echo.leave('ReportChannel.' + this.user.id);
      } catch (error) {
        console.error('Error cleaning up previous listeners:', error);
      }
    }

    echo.private('ReportChannel.' + this.user.id).listen('.report-event', (report: any) => {
        this.showReportBanner(report)
    });

    echo.private('ReportChannel.' + this.user.id).listen('.report-not-generated-event', () => {
        this.showReportNotGeneratedBanner()
    });

    echo.private('ReportChannel.' + this.user.id).listen('.hubspot-refresh-event', (report: any) => {
        this.showHubspotRefreshBanner(report)
    });

    echo.private('ReportChannel.' + this.user.id).listen('.report-finished', (data: any) => {
        this.sharedService.emitEvent(EventTypes.REPORT_FINISHED, { message: 'Report Finished', report_id: data.search_id, user_id: data.user_id });
    });

    echo.private('ReportChannel.' + this.user.id).listen('.report-search-error', (data: any) => {
      // Close the loading banner
      this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, { message: 'Close Report Banner' });

      // Show error popup with the specific error message from the backend
      const errorMessage = data.error_message || 'An error occurred while generating the report.';
      this.popupService.notify(errorMessage, 'Search Error', 'error');

      // Emit search error event for components that need to reset their state
      this.sharedService.emitEvent(EventTypes.SEARCH_ERROR, {
        search_id: data.search_id,
        error_code: data.error_code,
        error_message: data.error_message
      });

      // Also emit to search-person specific emitter so it can reset its flag
      this.sharedService.emitSearchPersonEvent(EventTypes.SEARCH_ERROR, {
        search_id: data.search_id,
        error_code: data.error_code,
        error_message: data.error_message
      });
    });

    /*
    this.echoService.getEcho().private('ReportChannel.' + this.user.id).listen('.search-report-completed', (report: any) => {

      console.log('[app.component.ts] Listen on search-report-completed');
      console.log('Report event field was listen', report);

    });
    */

  }

 // another function for hubspot event emit
  showHubspotRefreshBanner(report:any) {
    const reportId = report.id;
    const reportName = report.name

    this.sharedService.emitEvent(EventTypes.SHOW_HUBSPOT_REFRESH_BANNER, { message: 'Show Hubspot Refresh Banner', report_id: reportId, report_name: reportName });
    this.sharedService.emitEvent(EventTypes.RELOAD_HUBSPOT, { message: 'Show Hubspot Banner', report_id: reportId, report_name: reportName });
  }
  showReportBanner (report: any) {
    const reportId = report.id;
    const reportName = report.name

    // Get the current route to determine which emitter to use
    const currentRoute = this.router.url;

    // Emit route-specific event for search-person component
    if (currentRoute.includes('reports') || currentRoute.includes('report') || currentRoute.includes('dashboard')) {
      this.sharedService.emitSearchPersonEvent(EventTypes.SHOW_REPORT_BANNER, {
        message: 'Show Report Banner',
        report_id: reportId,
        report_name: reportName
      });
    } else if (currentRoute.includes('people')) {
      this.sharedService.emitPeopleEvent(EventTypes.SHOW_REPORT_BANNER, {
        message: 'Show Report Banner',
        report_id: reportId,
        report_name: reportName
      });
    }

    // Emit global events
    this.sharedService.emitEvent(EventTypes.SHOW_REPORT_BANNER, { message: 'Show Report Banner', report_id: reportId, report_name: reportName });
    this.sharedService.emitEvent(EventTypes.RELOAD_REPORTS, { message: 'Reload Reports', report_id: reportId, report_name: reportName });
    this.sharedService.emitEvent(EventTypes.UPDATE_CREDITS, { message: 'Update Credits' });
  }

  showReportNotGeneratedBanner () {

    this.sharedService.emitEvent(EventTypes.REPORT_NOT_GENERATED, { message: 'Show Report Not Generated Banner' });

  }

  ngOnDestroy() {
    // Signal all subscriptions to complete
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up any other resources
    this.cleanupEventListeners();
  }

  private cleanupEventListeners(): void {
    // Check if user exists before trying to leave the channel
    const echo = this.echoService.getEcho();
    if (this.user && this.user.id && echo) {
      try {
        echo.leave('ReportChannel.' + this.user.id);
      } catch (error) {
        console.error('Error leaving Echo channel:', error);
      }
    }
  }

  private setupRecoveryMode(): void {
    // Check if we're in recovery mode
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('recover')) {
      // Clear all storage
      try {
        localStorage.clear();
        sessionStorage.clear();

        // Redirect to login
        window.location.href = '/login';
      } catch (error) {
        console.error('Error during recovery:', error);
      }
    }
  }

  private monitorForFreezes(): void {
    let lastActiveTime = Date.now();

    // Update on user activity
    const updateActivity = () => { lastActiveTime = Date.now(); };
    document.addEventListener('click', updateActivity);
    document.addEventListener('keydown', updateActivity);
    document.addEventListener('mousemove', updateActivity);

    // Check for potential freezes every 10 seconds
    const intervalId = setInterval(() => {
      const now = Date.now();
      if (now - lastActiveTime > 30000) { // 30 seconds without activity
        console.warn('Potential UI freeze detected!');
      }
    }, 10000);

    // Add the interval to subscriptions for cleanup
    this.subscriptions.push({
      unsubscribe: () => clearInterval(intervalId)
    } as Subscription);
  }

}
